/**
 * Composable for getting inventory images with GCS fallback
 * Prefers GCS URLs (permanent), falls back to eBay URLs (temporary)
 */

/**
 * Get image URL for inventory item, preferring GCS over eBay
 * @param {Object} item - Inventory item from API
 * @param {string} size - Size variant (thumb, small, medium, large, original)
 * @returns {string} Image URL
 */
export function getInventoryImageUrl(item, size = 'medium') {
  if (!item) {
    console.warn('[useInventoryImages] No item provided');
    return '';
  }

  // Try GCS URLs first (permanent storage)
  try {
    const gcsUrls = typeof item.photo_urls_gcs === 'string'
      ? JSON.parse(item.photo_urls_gcs)
      : item.photo_urls_gcs;

    if (gcsUrls && gcsUrls[size]) {
      return gcsUrls[size];
    }
    // Fallback to other sizes if requested size not available
    if (gcsUrls && (gcsUrls.medium || gcsUrls.original)) {
      return gcsUrls.medium || gcsUrls.original;
    }
  } catch (e) {
    // Parse error, fall through to eBay URL
    console.debug('[useInventoryImages] GCS URL parse error:', e.message);
  }

  // Fallback to eBay URL (temporary - expires after 90 days)
  if (item.photo_url) {
    // Convert eBay URL to specific size
    const match = item.photo_url.match(/z\/([a-zA-Z0-9-~_]+)\/\$/);
    if (match) {
      const sizeMap = {
        thumb: 's-l140',
        small: 's-l400',
        medium: 's-l800',
        large: 's-l1600',
        original: 's-l1600'
      };
      const ebaySize = sizeMap[size] || 's-l800';
      return `https://i.ebayimg.com/images/g/${match[1]}/${ebaySize}.jpg`;
    }
    return item.photo_url;
  }

  console.warn('[useInventoryImages] No photo_url found for item:', item.SKU || item.id);
  return '';
}

/**
 * Get gallery image URLs, preferring GCS over eBay
 * @param {Object} item - Inventory item from API
 * @param {string} size - Size variant (thumb, small, medium, large, original)
 * @returns {Array<string>} Array of image URLs
 */
export function getInventoryGalleryUrls(item, size = 'medium') {
  if (!item) return [];

  // Try GCS gallery first
  try {
    const gcsGallery = typeof item.photo_gallery_gcs === 'string'
      ? JSON.parse(item.photo_gallery_gcs)
      : item.photo_gallery_gcs;

    if (Array.isArray(gcsGallery) && gcsGallery.length > 0) {
      return gcsGallery
        .map(imgObj => imgObj[size] || imgObj.medium || imgObj.original)
        .filter(Boolean);
    }
  } catch (e) {
    // Parse error, fall through to eBay URLs
  }

  // Fallback to eBay gallery URLs
  try {
    const ebayGallery = typeof item.photo_gallery === 'string'
      ? JSON.parse(item.photo_gallery)
      : item.photo_gallery;

    if (Array.isArray(ebayGallery)) {
      // Convert eBay URLs to requested size
      return ebayGallery.map(url => {
        const match = url.match(/z\/([a-zA-Z0-9-~_]+)\/\$/);
        if (match) {
          const sizeMap = {
            thumb: 's-l140',
            small: 's-l400',
            medium: 's-l800',
            large: 's-l1600',
            original: 's-l1600'
          };
          const ebaySize = sizeMap[size] || 's-l800';
          return `https://i.ebayimg.com/images/g/${match[1]}/${ebaySize}.jpg`;
        }
        return url;
      });
    }
  } catch (e) {
    return [];
  }

  return [];
}

/**
 * Check if item has GCS images migrated
 * @param {Object} item - Inventory item
 * @returns {boolean}
 */
export function hasGcsImages(item) {
  return item?.image_migration_status === 'migrated' && !!item?.photo_urls_gcs;
}

/**
 * Get migration status badge info
 * @param {Object} item - Inventory item
 * @returns {Object} { text, variant, icon }
 */
export function getMigrationStatus(item) {
  if (!item?.image_migration_status) {
    return { text: 'Pending', variant: 'secondary', icon: 'clock' };
  }

  const statusMap = {
    migrated: { text: 'Cloud', variant: 'success', icon: 'cloud-check' },
    pending: { text: 'Pending', variant: 'warning', icon: 'clock' },
    failed: { text: 'Failed', variant: 'danger', icon: 'exclamation-triangle' }
  };

  return statusMap[item.image_migration_status] || statusMap.pending;
}
